'use client';

import React, { useCallback, useMemo } from 'react';
import { useFiltersLocalState } from '../SearchFiltersList/useFiltersLocalState';
import { BaseSearch } from '../SearchFiltersList/BaseSearch';
import { useGeoSearchString } from '@/contexts/CookieContext';
import { convertParamsToPath } from '@/utils/SEOPathModule/convertParamsToPath';
import { useRouter } from '@/hooks/useRouter';
import { GeoSelector } from '../GeoSelector';
import { usePathname } from '@/navigation';
import { useLocale } from 'next-intl';
import { convertUrl } from '../../main-page/CategoriesShortcuts/convertUrl';
import { runtimeConfig } from '@/config/runtime-config';
import { Dicts } from '@/utils/SEOPathModule/types';
import { useGetCategoryIdDict } from '@/hooks/useGetCategoryIdDict';
import { Skeleton } from '@/components/ui/skeleton';
import { useIsClient } from 'usehooks-ts';

type InlineComponentProps = {
  categoryIdDict: Dicts['categoryIdDict'];
};

const RegularPageSearchInline: React.FC<InlineComponentProps> = ({ categoryIdDict }) => {
  const router = useRouter();
  const locale = useLocale();
  const pathname = usePathname();

  const isMainPage = useMemo(() => {
    if (!pathname) {
      return false;
    }

    const normalizedPathname = pathname === '/' ? '/' : pathname.replace(/\/$/, '');
    return normalizedPathname === '/' || normalizedPathname === `/${locale}`;
  }, [locale, pathname]);

  const initialValues = useMemo(() => {
    return { params: [], commonParams: {}, categories: [] };
  }, []);

  const {
    shownParameters,
    priceValue,
    onChangePrice,
    onChangeIsFree,
    onUpdateParam,
    onUpdateCategory,
    onChangeSearch,
    searchValue,
    onChangeSort,
    isFree,
    filtersLocalValue,
  } = useFiltersLocalState({
    initialValue: initialValues,
    initialDicts: {
      categoryIdDict: categoryIdDict,
    },
  });

  const geoSearchStr = useGeoSearchString();

  const onSubmit = useCallback(
    (customGeoSearch?: string) => {
      const finalUrl = convertParamsToPath(
        {
          commonParams: filtersLocalValue.commonParams,
          categories: filtersLocalValue.categories,
          parameters: filtersLocalValue.params,
        },
        { categoryIdDict: categoryIdDict },
        customGeoSearch === undefined ? geoSearchStr : customGeoSearch,
      );

      router.push(finalUrl);
    },
    [filtersLocalValue, geoSearchStr, router, categoryIdDict],
  );

  const onSubmitHandler = useCallback(() => {
    onSubmit();
  }, [onSubmit]);

  const onSelectGeo = useCallback(
    async (location: any) => {
      if (!isMainPage) {
        return;
      }
      const wlMode = !runtimeConfig.isMultiCountry;
      const resultUrl = await convertUrl({
        countryId: location.countryId,
        regionId: location.regionId,
        cityId: location.cityId,
        isWLMode: wlMode,
      });

      onSubmit(resultUrl);
    },
    [isMainPage, onSubmit],
  );

  return (
    <>
      <BaseSearch
        shownParameters={shownParameters}
        searchValue={searchValue || ''}
        params={filtersLocalValue.params}
        price={priceValue}
        isFree={isFree}
        categories={filtersLocalValue.categories}
        onChangePrice={onChangePrice}
        onChangeIsFree={onChangeIsFree}
        onUpdateCategory={onUpdateCategory}
        onUpdateParam={onUpdateParam}
        onClickSearch={onSubmitHandler}
        onSearchChange={onChangeSearch}
        sort={filtersLocalValue.commonParams.sortBy || ''}
        onChangeSort={onChangeSort}
        onSelectGeo={onSelectGeo}
      />

      <div className='mb-[4px] lg:hidden'>
        <GeoSelector onSelectGeo={onSelectGeo} />
      </div>
    </>
  );
};

export const RegularPageSearch: React.FC = () => {
  const { data: categoryIdDict, isLoading } = useGetCategoryIdDict();
  const isClient = useIsClient();

  return !isClient || isLoading || Object.keys(categoryIdDict).length === 0 ? (
    <Skeleton className='lg:h-[66px] w-full h-[80px]' />
  ) : (
    <RegularPageSearchInline categoryIdDict={categoryIdDict} />
  );
};
